import React from 'react';
import { useIntl } from 'react-intl';
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, BarChart } from 'recharts';
import { TRPCClientErrorLike } from '@trpc/client';
import { DefaultErrorShape } from '@trpc/server/unstable-core-do-not-import';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faChartBar } from '@fortawesome/pro-light-svg-icons/faChartBar';
import { TypedMemo } from '../../../common/types';
import { ChartSkeleton } from '../ChartSkeleton/ChartSkeleton';
import { useChartTokens } from '../../theme/useChartTokens';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';

interface ScoreData {
  date: string;
  gainScore: number;
  lossScore: number;
}

interface DateRange {
  start: string;
  end: string;
}

interface ChartData {
  date: string;
  gainScore: number;
  lossScore: number;
  lossScorePositive: number;
}

interface PlaylistScoreChartProps {
  title: string;
  data: ScoreData[];
  dateRange?: DateRange;
  isLoading: boolean;
  error: TRPCClientErrorLike<{ transformer: true; errorShape: DefaultErrorShape }> | null;
}

// Custom tooltip component defined outside of render. Chart colors are passed
// down as props because hooks cannot be called inside this sub-component.
const CustomTooltip = ({ active, payload, label, intl, posColor, negColor, bgColor, txColor, borderColor }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        className="p-3 rounded shadow"
        style={{ backgroundColor: bgColor, color: txColor, border: `1px solid ${borderColor}` }}
      >
        <p className="mb-2" style={{ fontWeight: 500 }}>
          {label}
        </p>
        <div style={{ fontSize: '14px' }}>
          <p className="font-monospace" style={{ color: posColor, margin: '4px 0' }}>
            {intl.formatMessage({
              id: 'app.sml.chart.gain-score',
              defaultMessage: 'Gain Score',
            })}
            :{' '}
            {
              data[
                intl.formatMessage({
                  id: 'app.sml.chart.gain-score',
                  defaultMessage: 'Gain Score',
                })
              ]
            }
          </p>
          <p className="font-monospace" style={{ color: negColor, margin: '4px 0' }}>
            {intl.formatMessage({
              id: 'app.sml.chart.loss-score',
              defaultMessage: 'Loss Score',
            })}
            : {data.lossScorePositive}
          </p>
        </div>
      </div>
    );
  }

  return null;
};

const PlaylistScoreChartComponent: React.FC<PlaylistScoreChartProps> = ({
  title,
  data,
  dateRange,
  isLoading,
  error,
}) => {
  const intl = useIntl();
  const chart = useChartTokens();

  // Transform data for the chart
  const chartData: ChartData[] = React.useMemo(() => {
    const gainScoreLabel = intl.formatMessage({
      id: 'app.sml.chart.gain-score',
      defaultMessage: 'Gain Score',
    });

    const lossScoreLabel = intl.formatMessage({
      id: 'app.sml.chart.loss-score',
      defaultMessage: 'Loss Score',
    });

    return data.map((item) => ({
      date: new Date(item.date).toLocaleDateString(intl.locale, {
        month: 'short',
        day: 'numeric',
      }),
      gainScore: item.gainScore,
      lossScore: -Math.abs(item.lossScore), // Make loss scores negative for chart
      lossScorePositive: Math.abs(item.lossScore), // Keep positive for tooltip
      [gainScoreLabel]: item.gainScore,
      [lossScoreLabel]: Math.abs(item.lossScore),
    }));
  }, [data, intl]);

  // Format date range for display
  const dateRangeText = React.useMemo(() => {
    if (!dateRange) return '';

    const startFormatted = new Date(dateRange.start).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
    });
    const endFormatted = new Date(dateRange.end).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return `${startFormatted} - ${endFormatted}`;
  }, [dateRange, intl.locale]);

  return (
    <Panel className="flex-grow-1" style={{ minHeight: '318px' }}>
      <PanelHead title={title} subtitle={dateRangeText} />
      <PanelBody>
        {isLoading ? (
          <div style={{ height: '250px' }}>
            <ChartSkeleton />
          </div>
        ) : error ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: '250px', color: chart.tx2 }}
          >
            <div className="text-center">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mb-2"
                style={{ fontSize: '2rem', color: chart.neg }}
              />
              <div style={{ fontSize: '14px' }}>
                {intl.formatMessage({
                  id: 'app.sml.chart.error',
                  defaultMessage: 'Error loading chart data',
                })}
              </div>
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: '250px', color: chart.tx2 }}
          >
            <div className="text-center">
              <FontAwesomeIcon icon={faChartBar} className="mb-2" style={{ fontSize: '2rem' }} />
              <div style={{ fontSize: '14px' }}>
                {intl.formatMessage({
                  id: 'app.sml.chart.no-data',
                  defaultMessage: 'No data available for this period',
                })}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ height: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 10,
                  right: 10,
                  left: 10,
                  bottom: 10,
                }}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="2 5" stroke={chart.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: chart.label }}
                  axisLine={{ stroke: chart.grid }}
                  tickLine={{ stroke: chart.grid }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: chart.axis }}
                  axisLine={{ stroke: chart.grid }}
                  tickLine={{ stroke: chart.grid }}
                />
                <Tooltip
                  content={
                    <CustomTooltip
                      intl={intl}
                      posColor={chart.pos}
                      negColor={chart.neg}
                      bgColor={chart.tooltipBg}
                      txColor={chart.tooltipTx}
                      borderColor={chart.line}
                    />
                  }
                />
                <ReferenceLine y={0} stroke={chart.zero} strokeWidth={1} />
                <Bar
                  dataKey="gainScore"
                  fill={chart.pos}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={22}
                  name={intl.formatMessage({
                    id: 'app.sml.chart.gain-score',
                    defaultMessage: 'Gain Score',
                  })}
                />
                <Bar
                  dataKey="lossScore"
                  fill={chart.neg}
                  radius={[0, 0, 3, 3]}
                  maxBarSize={22}
                  name={intl.formatMessage({
                    id: 'app.sml.chart.loss-score',
                    defaultMessage: 'Loss Score',
                  })}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const PlaylistScoreChart = TypedMemo(PlaylistScoreChartComponent);
